import {FormulaEngine} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaEngine';
import {OhmParseInputResult} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmParseResult';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {ClientFunction} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {ISelectionTextFocusActivator} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FocusActivator';
import {
    FormulaInputTokenType,
    ICellReferenceExternalFormulaToken,
    ICellReferenceFormulaToken,
    ICellReferenceRangeExternalFormulaToken,
    ICellReferenceRangeFormulaToken,
    IFormulaToken,
    ILinearFormulaToken
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {MeshRange, ReferencedRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {
    MeshTypeaheadItem,
    MeshTypeaheadItemKind,
    TypeaheadItemCustomAction
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshTypeaheadItem';
import {R1C1Location, R1C1Range} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/R1C1';
import {SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {IListItem, ListModel} from '@mintdata/mint-kit/api/models/List';
import {TextSelection} from '@mintdata/mint-kit/api/models/Text';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, IReactionDisposer, reaction} from 'mobx';

const formulaInputStyles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/input/FormulaInput.less');
const cx = classnames.bind(formulaInputStyles);

export class MeshInputController
{
    private readonly _formulaEngine:FormulaEngine = FormulaEngine.getInstance();

    public focusInputControl():void
    {
        const {inputActivator} = controllers.ui.spreadsheets.main.getEditorState().input;

        if( inputActivator )
            inputActivator.focus();
    }

    // Invoked from React view when DOM ref is ready
    public setInputActivator(inputActivator:ISelectionTextFocusActivator):void
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        inputState.inputActivator = inputActivator;

        this.setIsInputJustMounted(!!inputActivator);
    }

    public setTextInInputControl(value:string):void
    {
        const {inputActivator} = controllers.ui.spreadsheets.main.getEditorState().input;

        if( inputActivator )
            inputActivator.setText(value);
    }

    public addTextInInputControl(ctx:IWSPMeshContext, text:string):void
    {
        const {inputActivator} = controllers.ui.spreadsheets.main.getEditorState().input;

        if( inputActivator )
        {
            const oldText:string = inputActivator.getText();
            const oldSelection:TextSelection = inputActivator.getSelection();

            let newText:string = oldText.slice(0, oldSelection.startIndex) +
                text +
                oldText.slice(typeof oldSelection.endIndex === 'number' ? oldSelection.endIndex : oldSelection.startIndex);
            let newSelection:TextSelection = new TextSelection(oldSelection.startIndex + text.length, oldSelection.endIndex);

            this.setActiveCellFormula(ctx, newText, newSelection, true);
        }
    }

    public selectTextInInputControl(selection:TextSelection):void
    {
        const {inputActivator} = controllers.ui.spreadsheets.main.getEditorState().input;

        if( inputActivator )
            inputActivator.setSelection(selection);
    }

    @bind
    private _parseFormula(formula:SlotFormula, location:SlotLocation):OhmParseInputResult
    {
        const engine = this._formulaEngine;
        const formulaEvalResult:OhmParseInputResult = engine.parseInputA1(formula, location);

        return formulaEvalResult;
    }

    @bind
    private _parseResultToLinearTokens(parseResult:OhmParseInputResult):ILinearFormulaToken[]
    {
        const fic = controllers.ui.spreadsheets.formulaInput;
        return fic.getPlainFormulaTokens(parseResult.result);
    }

    @bind
    public setActiveCellFormula(ctx:IWSPMeshContext,
                                newFormula:SlotFormula,
                                textSelection:TextSelection,
                                isUserInput:boolean = false):void
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        const location = new SlotLocation(inputState.colIndex, inputState.rowIndex);

        const prevFunctionToken:ILinearFormulaToken = inputState.currentFunctionToken;

        action(() =>
        {
            const ohmParseResult = this._parseFormula(newFormula, location);
            const formulaTokens = this._parseResultToLinearTokens(ohmParseResult);

            inputState.formulaTokens = formulaTokens;
            inputState.isValueEdited = true;

            this._setIsUserInput(isUserInput);

            if( !inputState.textSelection.isEqual(textSelection) )
                inputState.textSelection = textSelection;

            if( formulaTokens && formulaTokens.length >= 1 )
            {
                const lastToken = formulaTokens[formulaTokens.length - 1];
                if( lastToken.token.type == FormulaInputTokenType.ParenthesesEnd )
                {
                    this._clearTypeaheadItems();
                }
            }
        })();

        this.setTextInInputControl(this._getFormulaTokensString());
        this.selectTextInInputControl(textSelection);

        if( !isUserInput )
            this._clearTypeaheadItems();
        else
        {
            if( this._isOpenParenAutoCompletion() )
            {
                requestAnimationFrame(() =>
                {
                    this.applyTypeaheadItem(ctx, this._getCurrentTypeaheadItem());
                });
            }
            else if( this._isCurrentFunctionTokenDifferentFromPrev(prevFunctionToken) )
                this.showFunctionTypeahead(ctx);
            else
                this._clearTypeaheadItems();
        }
    }

    private _getCurrentTypeaheadItem():MeshTypeaheadItem<any> | null
    {
        const {typeaheadList} = controllers.ui.spreadsheets.main.getEditorState().input;

        const selectedItem:IListItem<MeshTypeaheadItem<any>> = typeaheadList && typeaheadList.getFirstSelectedItem();

        return selectedItem ? selectedItem.getData() : null;
    }

    private _isOpenParenAutoCompletion():boolean
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        const tokenAtCursor = inputState.tokenAtCursor;

        if( inputState.isTypeaheadOpen &&
            tokenAtCursor &&
            tokenAtCursor.token.type === FormulaInputTokenType.ParenthesesStart )
        {
            const currentItem = this._getCurrentTypeaheadItem();
            const userInput = inputState.currentFunctionToken ? inputState.currentFunctionToken.token.value : null;

            if( !userInput || userInput.trim().length === 0 )
                return false;

            const ciUserInput = userInput.toUpperCase();

            if( currentItem.kind === MeshTypeaheadItemKind.ClientFunction )
            {
                const cf = currentItem.datum as ClientFunction;

                if( cf.name.toUpperCase().startsWith(ciUserInput) ||
                    cf.getQualifiedName().toUpperCase().startsWith(ciUserInput) )
                {
                    return true;
                }
            }
        }

        return false;
    }

    private _isCurrentFunctionTokenDifferentFromPrev(prevFunctionToken:ILinearFormulaToken)
    {
        const {currentFunctionToken} = controllers.ui.spreadsheets.main.getEditorState().input;

        return currentFunctionToken &&
            (!prevFunctionToken ||
                prevFunctionToken.token.value !== currentFunctionToken.token.value);
    }

    private _getFormulaTokensString():string
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        const {
            formulaTokens,
            colIndex,
            rowIndex,
            isSelectCellRefMode,
            tokenAtCursor
        } = inputState;

        const {meshInput} = controllers.ui.spreadsheets;

        if( formulaTokens )
            return formulaTokens.map((formulaToken:ILinearFormulaToken):string =>
                {
                    if( formulaToken && formulaToken.token )
                    {
                        const classNames:string = cx(
                            'formulaToken',
                            formulaToken.token.type
                        );
                        const isHighlighted:boolean = meshInput._isFormulaTokenHighlighted(formulaToken);
                        const isCellRefPlaceholder:boolean = isSelectCellRefMode && formulaToken === tokenAtCursor;
                        const isCellRefToken:boolean = formulaToken.token.type === FormulaInputTokenType.CellReference
                            || formulaToken.token.type === FormulaInputTokenType.CellReferenceRange;
                        const cellRefTokenColor:string = meshInput._getCellRefFormulaTokenColor(formulaToken);

                        return `<span
                                    token-highlighted="${isHighlighted}"
                                    token-cellref-placeholder="${isCellRefPlaceholder}"
                                    token-cellref="${isCellRefToken}"
                                    class="${classNames}"
                                    style="color:${cellRefTokenColor};"
                                >${meshInput._getTokenHtmlValue(
                            formulaToken.token,
                            colIndex,
                            rowIndex
                        )}</span>`;
                    }

                    return '';
                }
            ).join('');

        return '';
    }

    private _highlightInlineEditorFormulaTokens(ctx:IWSPMeshContext):void
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        const {formulaTokens, isSelectCellRefMode, tokenAtCursor} = inputState;
        const {meshInput} = controllers.ui.spreadsheets;

        const tokenElements:NodeListOf<Element> = document.querySelectorAll(
            `[id='${ctx.sheetId}'] .${formulaInputStyles.formulaToken}`
        );

        if( tokenElements && tokenElements.length > 0 )
        {
            for( let i = 0; i < tokenElements.length; i++ )
            {
                const formulaToken:ILinearFormulaToken = formulaTokens[i];

                if( formulaToken && tokenElements[i] )
                {
                    const isHighlighted:boolean = meshInput._isFormulaTokenHighlighted(formulaToken);
                    const isCellRefPlaceholder:boolean = isSelectCellRefMode && formulaToken === tokenAtCursor;

                    tokenElements[i].setAttribute('token-highlighted', String(isHighlighted));
                    tokenElements[i].setAttribute('token-cellref-placeholder', String(isCellRefPlaceholder));
                }
            }
        }
    }

    @action
    public setNewCellRef(ctx:IWSPMeshContext,
                         formulaTokens:Array<ILinearFormulaToken>,
                         colIndex:number,
                         rowIndex:number,
                         isCellRefEdit:boolean,
                         tokenAtCursor:ILinearFormulaToken,
                         displayString:string):void
    {
        const formula:string = controllers.ui.spreadsheets.formulaInput.getFormulaTokensText(
            formulaTokens,
            colIndex,
            rowIndex,
            true
        );

        const lhs = `${formula.substring(0, isCellRefEdit ? tokenAtCursor.token.startIndex : tokenAtCursor.token.endIndex)}`;
        const rhs = `${formula.substring(tokenAtCursor.token.endIndex)}`;
        const newFormula:string = `${lhs}${displayString}${rhs}`;

        const newCaretPosition:number = (isCellRefEdit
            ? tokenAtCursor.token.startIndex
            : tokenAtCursor.token.endIndex)
            + displayString.length;

        this.setActiveCellFormula(
            ctx,
            newFormula,
            new TextSelection(newCaretPosition, null),
            true
        );
    }

    @action
    public selectNewCellRef(ctx:IWSPMeshContext, deltaCol:number, deltaRow:number, isRelativeToPreviousCellRef:boolean = true):boolean
    {
        const {main, mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const {
            formulaTokens,
            tokenAtCursor,
            isSelectCellRefMode
        } = main.getEditorState().input;

        const {
            details: {
                activeCell: {
                    colIndex,
                    rowIndex
                }
            },
            size: {
                numCols,
                numRows
            }
        } = meshState;

        const {meshSelection} = controllers.ui.spreadsheets;

        if( isSelectCellRefMode && tokenAtCursor )
        {
            let isCellRefEdit:boolean = false;
            let prevLocation:R1C1Location;

            if( tokenAtCursor.token.type === FormulaInputTokenType.CellReference
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceExternal
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRange
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRangeExternal )
            {
                if( isRelativeToPreviousCellRef )
                    prevLocation = (tokenAtCursor.token as ICellReferenceRangeFormulaToken).range.startLocation;

                isCellRefEdit = true;
            }

            if( !prevLocation )
            {
                prevLocation = new R1C1Location(
                    0,
                    false,
                    0,
                    false
                );
            }

            const newColIndex:number = deltaCol != null ? ((prevLocation.isColumnFixed) ? prevLocation.columnOffset + deltaCol - 1 : colIndex + prevLocation.columnOffset + deltaCol) : null;
            const newRowIndex:number = deltaRow != null ? ((prevLocation.isRowFixed) ? prevLocation.rowOffset + deltaRow - 1 : rowIndex + prevLocation.rowOffset + deltaRow) : null;

            if( (newColIndex == null || (newColIndex >= 0 && newColIndex < numCols))
                && (newRowIndex == null || (newRowIndex >= 0 && newRowIndex < numRows)) )
            {
                const location:R1C1Location = new R1C1Location(
                    deltaRow != null ? prevLocation.rowOffset + deltaRow : null,
                    prevLocation.isRowFixed,
                    deltaCol != null ? prevLocation.columnOffset + deltaCol : null,
                    prevLocation.isColumnFixed
                );
                const displayString:string = deltaCol == null || deltaRow == null
                    ? new R1C1Range(
                        location,
                        location
                    ).toA1String(colIndex, rowIndex)
                    : location.toA1String(colIndex, rowIndex);

                this.setNewCellRef(
                    ctx,
                    formulaTokens,
                    colIndex,
                    rowIndex,
                    isCellRefEdit,
                    tokenAtCursor,
                    displayString
                );

                meshSelection.checkActiveCellCol(ctx, newColIndex);
                meshSelection.checkActiveCellRow(ctx, newRowIndex);
            }

            return true;
        }

        return false;
    }

    @action
    public selectNewCellRefRange(ctx:IWSPMeshContext, deltaCol:number, deltaRow:number, isRelativeToPreviousCellRef:boolean = true):boolean
    {
        const {main, mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const {
            formulaTokens,
            tokenAtCursor,
            isSelectCellRefMode
        } = main.getEditorState().input;

        const {
            details: {
                activeCell: {
                    colIndex,
                    rowIndex
                }
            },
            size: {
                numCols,
                numRows
            }
        } = meshState;

        const {meshSelection} = controllers.ui.spreadsheets;

        if( isSelectCellRefMode && tokenAtCursor )
        {
            let isCellRefEdit:boolean = false;
            let prevRange:R1C1Range;

            if( tokenAtCursor.token.type === FormulaInputTokenType.CellReference
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceExternal
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRange
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRangeExternal )
            {
                prevRange = (tokenAtCursor.token as ICellReferenceRangeFormulaToken).range;

                if( !isRelativeToPreviousCellRef )
                    prevRange = new R1C1Range(
                        prevRange.startLocation,
                        new R1C1Location(
                            0,
                            false,
                            0,
                            false
                        )
                    );

                isCellRefEdit = true;
            }
            else
            {
                const currentLocation:R1C1Location = new R1C1Location(
                    0,
                    false,
                    0,
                    false
                );
                prevRange = new R1C1Range(
                    currentLocation,
                    currentLocation
                );
            }

            const newColIndex:number = deltaCol != null ? colIndex + prevRange.endLocation.columnOffset + deltaCol : null;
            const newRowIndex:number = deltaRow != null ? rowIndex + prevRange.endLocation.rowOffset + deltaRow : null;

            if( (newColIndex == null || newColIndex >= 0 && newColIndex < numCols)
                && (newRowIndex == null || newRowIndex >= 0 && newRowIndex < numRows) )
            {
                const range:R1C1Range = new R1C1Range(
                    prevRange.startLocation,
                    new R1C1Location(
                        deltaRow != null ? prevRange.endLocation.rowOffset + deltaRow : null,
                        prevRange.endLocation.isRowFixed,
                        deltaCol != null ? prevRange.endLocation.columnOffset + deltaCol : null,
                        prevRange.endLocation.isColumnFixed
                    )
                );

                const displayString:string = range.toA1String(colIndex, rowIndex);

                this.setNewCellRef(
                    ctx,
                    formulaTokens,
                    colIndex,
                    rowIndex,
                    isCellRefEdit,
                    tokenAtCursor,
                    displayString
                );

                meshSelection.checkActiveCellCol(ctx, newColIndex);
                meshSelection.checkActiveCellRow(ctx, newRowIndex);
            }

            return true;
        }

        return false;
    }

    @action
    public selectNewExternalCellRef(ctx:IWSPMeshContext, col:number, row:number, isRelativeToPreviousCellRef:boolean = true):boolean
    {
        const {main, mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const {
            formulaTokens,
            tokenAtCursor,
            isSelectCellRefMode,
            colIndex: inputColIndex,
            rowIndex: inputRowIndex,
        } = main.getEditorState().input;

        const {
            size: {
                numCols,
                numRows
            }
        } = meshState;

        const {meshSelection} = controllers.ui.spreadsheets;

        if( isSelectCellRefMode && tokenAtCursor )
        {
            let isCellRefEdit:boolean = false;
            let prevLocation:R1C1Location;

            if( tokenAtCursor.token.type === FormulaInputTokenType.CellReference
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceExternal
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRange
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRangeExternal )
            {
                if( isRelativeToPreviousCellRef )
                    prevLocation = (tokenAtCursor.token as ICellReferenceRangeExternalFormulaToken).range.startLocation;

                isCellRefEdit = true;
            }

            if( !prevLocation )
                prevLocation = new R1C1Location(0, false, 0, false);

            let newColIndex:number = col != null ? prevLocation.columnOffset + col : null;
            let newRowIndex:number = row != null ? prevLocation.rowOffset + row : null;

            if( isRelativeToPreviousCellRef )
            {
                newColIndex += prevLocation.isColumnFixed ? 0 : inputColIndex;
                newRowIndex += prevLocation.isRowFixed ? 0 : inputRowIndex;
            }

            const minColLimit = prevLocation.isColumnFixed ? 1 : 0;
            const minRowLimit = prevLocation.isRowFixed ? 1 : 0;

            const maxColLimit = prevLocation.isColumnFixed ? numCols + 1 : numCols;
            const maxRowLimit = prevLocation.isRowFixed ? numRows + 1 : numRows;

            if( (newColIndex == null || (newColIndex >= minColLimit && newColIndex < maxColLimit))
                && (newRowIndex == null || (newRowIndex >= minRowLimit && newRowIndex < maxRowLimit)) )
            {
                const currentLocation:R1C1Location = new R1C1Location(
                    newRowIndex,
                    prevLocation.isRowFixed,
                    newColIndex,
                    prevLocation.isColumnFixed
                );

                let sheetName = MeshUtils.sheetNameToRefSheetName(controllers.ui.spreadsheets.main.findAnySheetById(ctx.sheetId).name);

                const locationString:string = currentLocation.columnOffset == null || currentLocation.rowOffset == null
                    ? new R1C1Range(currentLocation, currentLocation).toA1String(0, 0)
                    : currentLocation.toA1String(0, 0);

                const displayString:string = `${sheetName}!${locationString}`;

                this.setNewCellRef(
                    ctx,
                    formulaTokens,
                    minColLimit + inputColIndex,
                    minRowLimit + inputRowIndex,
                    isCellRefEdit,
                    tokenAtCursor,
                    displayString
                );

                meshSelection.checkActiveCellCol(ctx, newColIndex);
                meshSelection.checkActiveCellRow(ctx, newRowIndex);
            }

            return true;
        }

        return false;
    }

    @action
    public selectNewExternalCellRefRange(ctx:IWSPMeshContext, col:number, row:number, isRelativeToPreviousCellRef:boolean = true):boolean
    {
        const {main, mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const {
            formulaTokens,
            tokenAtCursor,
            isSelectCellRefMode,
            colIndex: inputColIndex,
            rowIndex: inputRowIndex,
        } = main.getEditorState().input;

        const {
            size: {
                numCols,
                numRows
            }
        } = meshState;

        const {meshSelection} = controllers.ui.spreadsheets;

        if( isSelectCellRefMode && tokenAtCursor )
        {
            let isCellRefEdit:boolean = false;
            let prevRange:R1C1Range;

            if( tokenAtCursor.token.type === FormulaInputTokenType.CellReference
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceExternal
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRange
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRangeExternal )
            {
                prevRange = (tokenAtCursor.token as ICellReferenceRangeExternalFormulaToken).range;

                prevRange = new R1C1Range(
                    new R1C1Location(
                        prevRange.startLocation.rowOffset == null
                            ? null
                            : prevRange.startLocation.rowOffset + (prevRange.startLocation.isRowFixed ? 0 : inputRowIndex),
                        prevRange.startLocation.isRowFixed,
                        prevRange.startLocation.columnOffset == null
                            ? null
                            : prevRange.startLocation.columnOffset + (prevRange.startLocation.isColumnFixed ? 0 : inputColIndex),
                        prevRange.startLocation.isColumnFixed
                    ),
                    new R1C1Location(
                        prevRange.endLocation.rowOffset,
                        prevRange.endLocation.isRowFixed,
                        prevRange.endLocation.columnOffset,
                        prevRange.endLocation.isColumnFixed
                    )
                );

                isCellRefEdit = true;
            }
            else
            {
                const currentLocation:R1C1Location = new R1C1Location(0, false, 0, false);

                prevRange = new R1C1Range(
                    currentLocation,
                    currentLocation
                );
            }

            let newColIndex:number = col != null ? prevRange.endLocation.columnOffset + col : null;
            let newRowIndex:number = row != null ? prevRange.endLocation.rowOffset + row : null;

            if( isRelativeToPreviousCellRef )
            {
                newColIndex += prevRange.endLocation.isColumnFixed ? 0 : inputColIndex;
                newRowIndex += prevRange.endLocation.isRowFixed ? 0 : inputRowIndex;
            }
            else
            {
                newColIndex = col;
                newRowIndex = row;
            }

            const minColLimit = prevRange.endLocation.isColumnFixed ? 1 : 0;
            const minRowLimit = prevRange.endLocation.isRowFixed ? 1 : 0;

            const maxColLimit = prevRange.endLocation.isColumnFixed ? numCols + 1 : numCols;
            const maxRowLimit = prevRange.endLocation.isRowFixed ? numRows + 1 : numRows;

            if( (newColIndex == null || newColIndex >= minColLimit && newColIndex < maxColLimit)
                && (newRowIndex == null || newRowIndex >= minRowLimit && newRowIndex < maxRowLimit) )
            {
                const range:R1C1Range = new R1C1Range(
                    prevRange.startLocation,
                    new R1C1Location(
                        newRowIndex,
                        prevRange.endLocation.isRowFixed,
                        newColIndex,
                        prevRange.endLocation.isColumnFixed
                    )
                );

                let sheetName = MeshUtils.sheetNameToRefSheetName(controllers.ui.spreadsheets.main.findAnySheetById(ctx.sheetId).name);
        
                const displayString:string = `${sheetName}!${range.toA1String(0, 0)}`;

                this.setNewCellRef(
                    ctx,
                    formulaTokens,
                    minColLimit + inputColIndex,
                    minRowLimit + inputRowIndex,
                    isCellRefEdit,
                    tokenAtCursor,
                    displayString
                );

                meshSelection.checkActiveCellCol(ctx, newColIndex);
                meshSelection.checkActiveCellRow(ctx, newRowIndex);
            }

            return true;
        }

        return false;
    }

    @action
    public setTextSelection(ctx:IWSPMeshContext, textSelection:TextSelection):void
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        if( !inputState.textSelection.isEqual(textSelection) )
        {
            inputState.textSelection = textSelection;
            this._setIsUserInput(false);

            if( inputState.isInputJustMounted )
                this.setIsInputJustMounted(false);
        }

        this._highlightInlineEditorFormulaTokens(ctx);

        this.hideTypeahead();
    }

    @action
    public switchCurrentCellRefKind(ctx:IWSPMeshContext):void
    {
        const {formulaInput, main} = controllers.ui.spreadsheets;

        const {input: inputState} = main.getEditorState();

        const {colIndex, rowIndex, formulaTokens} = inputState;

        const currentCellRefToken = inputState.currentCellRefToken;
        if( currentCellRefToken )
        {
            const referenceToken = currentCellRefToken.token as ICellReferenceExternalFormulaToken;
            if( referenceToken && referenceToken.location && referenceToken.range )
            {
                referenceToken.location = this._switchReferenceKind(referenceToken.location, colIndex, rowIndex);
                referenceToken.range = new R1C1Range(referenceToken.location, referenceToken.location);
                referenceToken.value = referenceToken.location.toA1String(colIndex, rowIndex);

                const valueLength = referenceToken.sheetName
                    ? referenceToken.sheetName.length + 1 + referenceToken.value.length
                    : referenceToken.value.length;

                const newTextSelection = new TextSelection(referenceToken.startIndex + valueLength, null);
                const newFormulaText = formulaInput.getFormulaTokensText(formulaTokens, colIndex, rowIndex, true);
                this.setActiveCellFormula(ctx, newFormulaText, newTextSelection, true);
            }
            else
            {
                const rangeToken = currentCellRefToken.token as ICellReferenceRangeExternalFormulaToken;
                if( rangeToken && rangeToken.range )
                {
                    rangeToken.range = new R1C1Range(this._switchReferenceKind(rangeToken.range.startLocation, colIndex, rowIndex), this._switchReferenceKind(rangeToken.range.endLocation, colIndex, rowIndex));
                    rangeToken.value = rangeToken.range.toA1String(colIndex, rowIndex);

                    const valueLength = referenceToken.sheetName
                        ? referenceToken.sheetName.length + 1 + referenceToken.value.length  // Sheet1 + ! + A1
                        : referenceToken.value.length;

                    const newTextSelection = new TextSelection(rangeToken.startIndex + valueLength, null);
                    const newFormulaText = formulaInput.getFormulaTokensText(formulaTokens, colIndex, rowIndex, true);
                    this.setActiveCellFormula(ctx, newFormulaText, newTextSelection, true);
                }
            }
        }
    }

    private _switchReferenceKind(location:R1C1Location, colIndex:number, rowIndex:number):R1C1Location
    {
        const locationIndexes = location.getIndexes(colIndex, rowIndex);

        if( location.isRowFixed )
        {
            if( location.isColumnFixed )
            {
                // $A$1 => A$1
                return new R1C1Location(locationIndexes.rowIndex + 1, true, locationIndexes.colIndex - colIndex, false);
            }
            else
            {
                // A$1 => $A1
                return new R1C1Location(locationIndexes.rowIndex - rowIndex, false, locationIndexes.colIndex + 1, true);
            }
        }
        else
        {
            if( location.isColumnFixed )
            {
                // $A1 => A1
                return new R1C1Location(locationIndexes.rowIndex - rowIndex, false, locationIndexes.colIndex - colIndex, false);
            }
            else
            {
                // A1 => $A$1
                return new R1C1Location(locationIndexes.rowIndex + 1, true, locationIndexes.colIndex + 1, true);
            }
        }
    }

    private _isFormulaTokenHighlighted(formulaToken:ILinearFormulaToken):boolean
    {
        const {input: {tokenAtCursor, tokenAtCursorMatches}} = controllers.ui.spreadsheets.main.getEditorState();

        return formulaToken && tokenAtCursor
            && tokenAtCursor.matchTokenIndexes && tokenAtCursor.matchTokenIndexes.length > 0
            && (formulaToken === tokenAtCursor
                || tokenAtCursorMatches.indexOf(formulaToken) !== -1);
    }

    private _getCellRefFormulaTokenColor(formulaToken:ILinearFormulaToken):string
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        const {cellRefTokens, referencedRanges, colIndex, rowIndex} = inputState;

        if( cellRefTokens && referencedRanges
            && (formulaToken.token.type === FormulaInputTokenType.CellReference
                || formulaToken.token.type === FormulaInputTokenType.CellReferenceExternal
                || formulaToken.token.type === FormulaInputTokenType.CellReferenceRange
                || formulaToken.token.type === FormulaInputTokenType.CellReferenceRangeExternal) )
        {
            const cellRefRangeToken:ICellReferenceRangeExternalFormulaToken = formulaToken.token as ICellReferenceRangeExternalFormulaToken;
            const range:MeshRange = MeshUtils.getCellRefRangeFromR1C1(
                cellRefRangeToken.range,
                colIndex,
                rowIndex,
            );

            const rangeKey:string = (cellRefRangeToken.sheetName || '') + range.asKey();

            const referencedRange:ReferencedRange = referencedRanges.get(rangeKey);

            return referencedRange ? referencedRange.color : null;
        }

        return null;
    }

    private _getTokenHtmlValue(token:IFormulaToken, currentColumnIndex:number, currentRowIndex:number):string
    {
        switch( token.type )
        {
            case FormulaInputTokenType.CellReference:
                const location:R1C1Location = (token as ICellReferenceFormulaToken).location;
                return location.toA1String(currentColumnIndex, currentRowIndex);
            case FormulaInputTokenType.CellReferenceRange:
                const range:R1C1Range = (token as ICellReferenceRangeFormulaToken).range;
                return range.toA1String(currentColumnIndex, currentRowIndex);
            case FormulaInputTokenType.CellReferenceExternal:
                const extLocation:R1C1Location = (token as ICellReferenceExternalFormulaToken).location;
                const sheetName:string = (token as ICellReferenceExternalFormulaToken).sheetName;

                return `${sheetName}!${extLocation.toA1String(currentColumnIndex, currentRowIndex)}`;
            case FormulaInputTokenType.CellReferenceRangeExternal:
                const extRange:R1C1Range = (token as ICellReferenceRangeExternalFormulaToken).range;
                const sheetNameRange:string = (token as ICellReferenceRangeExternalFormulaToken).sheetName;

                return `${sheetNameRange}!${extRange.toA1String(currentColumnIndex, currentRowIndex)}`;
            default:
                return token.value
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#39;')
                    .replace(/ /g, '&nbsp;');
        }
    }

    @bind
    private _findFunctionTokenAtCursor():ILinearFormulaToken | null
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        let functionToken:ILinearFormulaToken = null;

        if( inputState.tokenAtCursor
            && (inputState.tokenAtCursor.token.type === FormulaInputTokenType.QualifiedFunctionName
                || inputState.tokenAtCursor.token.type === FormulaInputTokenType.SimpleFunctionName) )
            functionToken = inputState.tokenAtCursor;
        else if( inputState.tokenAtCursor && inputState.tokenBeforeTokenAtCursor
            && (inputState.tokenBeforeTokenAtCursor.token.type === FormulaInputTokenType.QualifiedFunctionName
                || inputState.tokenBeforeTokenAtCursor.token.type === FormulaInputTokenType.SimpleFunctionName) )
            functionToken = inputState.tokenBeforeTokenAtCursor;

        return functionToken;
    }

    private _setIsUserInput(isUserInput:boolean):void
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        inputState.isUserInput = isUserInput;
        inputState.isSelectAfterChange = isUserInput;
    }

    @action
    public setIsInputJustMounted(isInputJustMounted:boolean):void
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        inputState.isInputJustMounted = isInputJustMounted;
    }

    @action
    private _clearTypeaheadItems():void
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        inputState.isTypeaheadOpen = false;
        inputState.typeaheadList = null;
    }

    @action
    public clearTypeaheadItems():void
    {
        this._clearTypeaheadItems();
    }

    @action.bound
    public showFunctionTypeahead(ctx:IWSPMeshContext):void
    {
        const {ui: {visualDesigner: {lx: {isLxDevMode}}}} = rootState;
        const functionToken = this._findFunctionTokenAtCursor();

        if( functionToken )
        {
            const tokenValue = functionToken.token.value;
            // const flowPortItems:Array<MeshTypeaheadItem<IVisualStreamId>> = controllers.ui.flowBindings.findTypeaheadItems(tokenValue);

            let clientFunctions:ClientFunction[] = controllers.ui.spreadsheets.clientFunctions.searchForFunctions(tokenValue);

            if( !isLxDevMode )
                clientFunctions = clientFunctions.filter(
                    (clientFunction:ClientFunction) => clientFunction.namespace !== 'lx'
                );

            const typeAheadItems:MeshTypeaheadItem<any>[] =
                clientFunctions
                    .sort((a, b) => a.getQualifiedName().localeCompare(b.getQualifiedName()))
                    .map(fn => MeshTypeaheadItem.forClientFunction(fn));

            const {sampleDesign} = rootState.ui.visualDesigner;

            if( !sampleDesign )
            {
                const createNewCloudFunctionItem = this._buildNewCloudFunctionItem(ctx);

                if( createNewCloudFunctionItem && createNewCloudFunctionItem.title.toUpperCase()
                    .startsWith(tokenValue.toUpperCase()) )
                    typeAheadItems.unshift(createNewCloudFunctionItem);
            }

            this._initTypeaheadList(tokenValue, typeAheadItems);
        }
        else
        {
            this._clearTypeaheadItems();
        }
    }

    private _buildNewCloudFunctionItem(ctx:IWSPMeshContext):MeshTypeaheadItem<TypeaheadItemCustomAction>
    {
        const {feature, ui} = controllers;
        const {openDesign, flow, spreadsheets} = ui;

        if( !feature.areCloudFunctionsEnabled() )
            return null;

        const title = 'Create new flow...';
        const pillText = 'cloud';

        const handler:TypeaheadItemCustomAction = () =>
        {
            const design = openDesign.getOpenDesign();
            const {projectId, designId} = design.designRef;

            spreadsheets.mesh.abortEditingCell(ctx);

            this.hideTypeahead();

            flow.createFlowAndNavigate(projectId, designId);
        };

        return MeshTypeaheadItem.forCustomAction(title, '', pillText, handler);
    }

    @action
    private _initTypeaheadList(tokenValue:string, items:Array<MeshTypeaheadItem<any>>):void
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        if( items && items.length )
        {
            this._selectItem(items, tokenValue);

            // TODO: Make preselection for the DataSource

            inputState.typeaheadList = new ListModel(items);
            inputState.isTypeaheadOpen = true;
        }
        else
        {
            this._clearTypeaheadItems();
        }
    }

    private _selectItem(items:Array<MeshTypeaheadItem<any>>, tokenValue:string)
    {
        const ciTokenValue = (tokenValue || '').toUpperCase();

        let selectedItem = items.find((item:MeshTypeaheadItem<any>) => item.title.toUpperCase()
            .startsWith(ciTokenValue));
        if( !selectedItem )
        {
            selectedItem = items.find((item:MeshTypeaheadItem<any>) => item.title.toUpperCase()
                .indexOf(ciTokenValue) >= 0);
            if( !selectedItem )
                selectedItem = items[0];
        }

        if( selectedItem )
        {
            (selectedItem as any).isSelected = true;
        }
    }

    public applyTypeaheadItem(ctx:IWSPMeshContext, typeaheadItem:MeshTypeaheadItem<any>):void
    {
        if( !typeaheadItem )
            return;

        const kind = typeaheadItem.kind;

        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        if( kind === MeshTypeaheadItemKind.CustomAction )
        {
            typeaheadItem.datum();
        }
        else if( kind === MeshTypeaheadItemKind.FlowPort )
        {
            const {colIndex, rowIndex} = inputState;

            this.hideTypeahead();
            controllers.ui.spreadsheets.mesh.abortEditingCell(ctx);

            controllers.ui.flowBindings.bindSlotToFlowPort(ctx, colIndex, rowIndex, typeaheadItem);
        }
        else if( kind === MeshTypeaheadItemKind.ClientFunction )
        {
            const {
                formulaTokens,
                currentFunctionToken,
                currentFunctionTrailingLength,
                colIndex,
                rowIndex
            } = inputState;

            if( currentFunctionToken )
            {
                const formula:string = controllers.ui.spreadsheets.formulaInput.getFormulaTokensText(
                    formulaTokens,
                    colIndex,
                    rowIndex,
                    true
                );

                const currentTokenValue = currentFunctionToken.token.value;
                const currentFunctionName:string = null; //this._getFunction(currentTokenValue); // TODO: !!!

                let displayString = currentFunctionName ? currentFunctionName : typeaheadItem.toFormulaDisplayString();

                if( kind === MeshTypeaheadItemKind.ClientFunction )
                    displayString += currentFunctionTrailingLength > 0 ? '' : '(';

                const lhs = `${formula.substring(0, currentFunctionToken.token.startIndex)}`;
                const rhs = `${formula.substring(currentFunctionToken.token.endIndex)}`;

                const newFormula:string = `${lhs}${displayString}${rhs}`;

                const newCaretPosition:number = currentFunctionToken.token.startIndex
                    + displayString.length
                    + currentFunctionTrailingLength;

                this.focusInputControl();
                this.setActiveCellFormula(
                    ctx,
                    newFormula,
                    new TextSelection(newCaretPosition, null),
                    true
                );
                this.hideTypeahead();
            }
        }
    }

    // private _getFunction(funcName:string):string | null
    // {
    //     const {clientFunctions} = controllers.ui.spreadsheets;

    //     const similarFunctions:ClientFunction[] = clientFunctions.searchForFunctions(funcName);

    //     const filtered = similarFunctions.filter(
    //         (clientFunction:ClientFunction) => clientFunction.alias && clientFunction.alias.toUpperCase() === funcName.toUpperCase()
    //     );

    //     return filtered.length === 1 ? filtered[0].alias : null;
    // }

    @action.bound
    public hideTypeahead():void
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        inputState.isTypeaheadOpen = false;
    }

    @bind
    private _touchCurrentFunction():IFormulaToken
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        return inputState.currentFunctionToken && inputState.currentFunctionToken.token;
    }

    @bind
    public initCurrentFunctionReaction():IReactionDisposer
    {
        return reaction
        (
            () =>
            {
                return this._touchCurrentFunction();
            },
            (currentFunctionToken:IFormulaToken) =>
            {
                this._updateClientFunction(currentFunctionToken);
            }
        );
    }

    @bind
    private _updateClientFunction(currentFunctionToken:IFormulaToken)
    {
        if( currentFunctionToken )
        {
            const func:ClientFunction = controllers.ui.spreadsheets.clientFunctions.findFunctionByName(
                currentFunctionToken.type === FormulaInputTokenType.SimpleFunctionName
                    ? 'mint-functions' // TODO: Think about basic functions
                    : (currentFunctionToken.valueParts as any).pkg,
                currentFunctionToken.type === FormulaInputTokenType.SimpleFunctionName
                    ? currentFunctionToken.value
                    : (currentFunctionToken.valueParts as any).funcName
            );

            this._setDocFunction(func);
        }
    }

    @action.bound
    private _setDocFunction(func:ClientFunction):void
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        inputState.docFunction = !!func ? func : null;
    }

    @action.bound
    public setDocFunction(funcName:string):void
    {
        const cf = controllers.ui.spreadsheets.clientFunctions;

        const func:ClientFunction = cf.findFunctionByName('mint-functions', funcName);

        this._setDocFunction(func);
    }

    @action.bound
    public toggleDocCollapse():void
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        inputState.isDocCollapsed = !inputState.isDocCollapsed;
    }

    @action.bound
    public setDocVisibility(value:boolean):void
    {
        const {input: inputState} = controllers.ui.spreadsheets.main.getEditorState();

        inputState.isDocVisible = value;
    }
}
